import {
  IonBadge,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonRow,
  IonText,
} from '@ionic/react';
import { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Machine,
  MachineConnectionType,
  useListMachinesQuery,
} from '../../api/graphql/generated';
import { useSearchListQueryWithInfiniteScroll } from '../../api/hooks/infinite-scroll.hooks';
import { IntercomArticle } from '../../config/intercom';
import { MachineContextProvider } from '../../pages/machine/machine-context-provider';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { ActiveFilterPopoverContent } from '../ui/searchbar/active-filter-popover-content';
import {
  Searchbar,
  SearchbarPopoverChildrenProps,
} from '../ui/searchbar/searchbar';
import { IntercomHelp } from './../help/intercom-help';
import { MachineProgramStatus } from './machine-program-status';
import './machine.css';
import { useNavigateToMachine } from './machine.utils';
import { ScratchpadPlaylistContextProvider } from './playlist/scratchpad-playlist.hooks';

interface MachinesList {
  teamId: string;
}

interface MachineCardProps {
  machine: Machine;
}

const MachinesList = (props: MachinesList): ReactElement => {
  const { teamId } = props;
  const { i18n } = useTranslation();

  const [showActive, setShowActive] = useState<boolean>(true);

  const {
    result: { data, loading, error, refetch },
    infiniteScroll,
    search,
    searchTerm,
  } = useSearchListQueryWithInfiniteScroll({
    useQuery: useListMachinesQuery,
    input: { teamId, active: showActive },
    queryOptions: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  });

  const filterPopoverContent = (props: SearchbarPopoverChildrenProps) => {
    return (
      <ActiveFilterPopoverContent
        showActive={showActive}
        onActiveChange={(active) => {
          setShowActive(active);
          // eslint-disable-next-line react/prop-types
          props.close();
        }}
      />
    );
  };

  const noResultsActive = (
    <div className="full-height-width content-center">
      <IonText className="ion-padding ion-text-center" color="medium">
        <h2>{i18n.t('common:machine_add_new_message')}</h2>
        <IntercomHelp
          articleId={IntercomArticle.GETTING_STARTED}
          description={i18n.t('common:machine_prepare_cobot')}
        />
      </IonText>
    </div>
  );

  const noResultsFilteredOnInactive = useMemo(
    () => (
      <div className="ion-padding ion-text-center">
        <IonText color="medium">{i18n.t('common:no_results_found')}</IonText>
      </div>
    ),
    [i18n]
  );

  const hasSearchText = searchTerm !== undefined && searchTerm !== '';

  const machines = data?.listMachines.items ?? [];

  return (
    <>
      <Searchbar
        onSearch={search}
        filterPopoverContent={filterPopoverContent}
        showFilterIndicator={showActive === false}
      />
      <LoadingRetry
        loading={loading}
        error={error}
        refetch={refetch}
        results={{
          hasResults: machines.length > 0,
          noResultsComponent:
            showActive && !hasSearchText
              ? noResultsActive
              : noResultsFilteredOnInactive,
        }}
      >
        <IonGrid>
          <IonRow>
            {machines.map((machine) => {
              return (
                <IonCol
                  key={machine.id}
                  sizeXs={'12'}
                  sizeLg={'6'}
                  sizeXl={'4'}
                >
                  <MachineCard
                    machine={{
                      ...machine,
                    }}
                  />
                </IonCol>
              );
            })}
          </IonRow>
        </IonGrid>
      </LoadingRetry>
      {infiniteScroll}
    </>
  );
};

const MachineCard = (props: MachineCardProps): ReactElement => {
  const { machine } = props;
  const { active, id, teamId } = machine;
  const { i18n } = useTranslation();

  const onClick = useNavigateToMachine();

  const subtitle = useMemo(() => {
    if (machine.connectionType === MachineConnectionType.DIRECT) {
      return i18n.t('common:app_mode_direct_title');
    } else if (machine.demo) {
      return i18n.t('common:machine_demo');
    }
  }, [i18n, machine]);

  return (
    <MachineContextProvider machineId={id} teamId={teamId}>
      <ScratchpadPlaylistContextProvider machineId={id}>
        <IonCard
          className={active ? 'machine-active' : 'machine-inactive'}
          onClick={() => {
            onClick(machine);
          }}
        >
          <IonCardHeader>
            <div className="machine-list-card-container">
              <div className="machine-list-state">
                <IonCardSubtitle className="no-text-transform">
                  <MachineProgramStatus machine={machine} />
                </IonCardSubtitle>
              </div>
              <div className="machine-list-is-demo">
                {subtitle && (
                  <IonCardSubtitle className="no-text-transform">
                    <IonBadge>{subtitle}</IonBadge>
                  </IonCardSubtitle>
                )}
              </div>
              <div className="machine-list-card-title-container">
                <IonCardTitle>{machine.name}</IonCardTitle>
              </div>
            </div>
          </IonCardHeader>
        </IonCard>
      </ScratchpadPlaylistContextProvider>
    </MachineContextProvider>
  );
};

export { MachinesList };
